<template>
  <div class="hidden md:flex w-full h-full items-stretch relative bg-blue-400">
    <NavigationMenu disable-hover-trigger disable-pointer-leave-close>
        <a
          :href="homeUrl"
          class="hover:bg-blue-500 focus:bg-blue-500 relative flex min-w-max shrink-0 items-center text-nowrap h-full px-3 transition-all duration-200"
          aria-label="Goalbook Toolkit Home"
        >
          <img class="h-8 pr-2" src="./../assets/toolkit_circle.png" alt="toolkit icon" />
          <span class="hidden text-white text-xl xl:flex">Goalbook Toolkit</span>
        </a>
        <NavigationMenuList>
          <NavMenuItems 
            :is-hamburger="false"
          />
        </NavigationMenuList>
    </NavigationMenu>
    <div class="relative z-10 ml-auto h-full min-w-0">
      <NavigationMenu disable-hover-trigger disable-pointer-leave-close as-child class="[&>div]:min-w-0">
      <nav aria-label="Account">
      <NavigationMenuList>
        <NavigationMenuItem class="min-w-0">
          <!-- Below 919px the 256px name block no longer fits beside the main menu, so the button shrinks to the photo or an account icon -->
          <NavigationMenuTrigger v-if="userStore.userId" class="h-full w-64 max-w-full flex justify-start max-[918px]:w-auto" aria-label="User Menu">
              <img
                :src="profileImageUrl"
                alt=""
                v-if="profileImageUrl"
                class="inline h-7 w-7 pr-1 pt-1"
              />
              <span v-else class="material-icons-round hidden text-display-sm text-white max-[918px]:inline" aria-hidden="true">account_circle</span>
              <div class="flex flex-col min-w-0 max-[918px]:hidden" data-testid="navbar-user-name">
                <span class="leading-none max-w-56 overflow-hidden text-ellipsis text-white text-xl pl-2 fs-exclude">{{ userName }}</span>
                <small class="text-xs leading-3 text-white text-left pl-2 fs-exclude">{{ userTitle }}</small>
              </div>
          </NavigationMenuTrigger>
          <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-white" v-if="userStore.fetching"/>
          <NavigationMenuContent align="start">
            <AdminToggle class="ml-0 px-2.5 py-2" flagType="editor" v-if="userStore.isSuperAdmin" />
            <AdminToggle class="ml-0 px-2.5 py-2" flagType="beta" v-if="hasBetaAccess" />
            <hr class="my-2" v-if="userStore.isSuperAdmin || hasBetaAccess">
            <NavigationMenuLink v-if="showContentProfileSelector" class="router-link" as="button" @click="setIsProfileSelectorOpen(true)">{{userStore.isSuperAdmin || userStore.isTestUser || userStore.isGoalbookBusinessUser ? 'Content Profile Selector' : 'Select Your State'}}</NavigationMenuLink>
            <NavigationMenuLink v-if="userStore.isSuperAdmin || userStore.isTestUser || userStore.isGoalbookBusinessUser" class="router-link" as="button" @click="handleOpenVisibilityModal">Visibility Settings</NavigationMenuLink>
            <NavigationMenuLink v-if="showCustomizationDemos" class="router-link" as="button" @click="handleOpenCustomizationDemoModal">Customization Demos</NavigationMenuLink>
            <NavigationMenuLink v-if="userStore.isTestUser" class="router-link" as="button" @click="userStore.setLicenseSelectorOpen(true)">License Selector</NavigationMenuLink>
            <hr class="my-2" v-if="showContentProfileSelector || userStore.isTestUser">
            <NavigationMenuLink v-if="!userStore.isSuperAdmin" class="router-link" :href="accountUrl" @click="analytics.sendEvent('Clicked Account Settings', { ref: 'navbar' })">Account Settings</NavigationMenuLink>
            <NavigationMenuLink class="router-link" as-child @click="analytics.sendEvent('Clicked My Library', { ref: 'navbar' })">
              <router-link to="/library/all">My Library</router-link>
            </NavigationMenuLink>
            <NavigationMenuLink class="router-link" :href="signOutUrl" @click="analytics.sendEvent('Clicked Sign Out', { ref: 'navbar' })">Sign Out</NavigationMenuLink>
            <hr class="my-2" v-if="userStore.isSchoolAdmin || userStore.isGoalbookBusinessUser || hasPathwaysAccess">
            <NavigationMenuLink v-if="userStore.isSchoolAdmin && !visibilityStore.analyticsDashboardVisibility" class="router-link" @click="analytics.sendEvent('Clicked Toolkit User List', { ref: 'navbar' })" as-child>
              <router-link to="/admin/user_list">Toolkit User List</router-link>
            </NavigationMenuLink>
            <NavigationMenuLink v-if="(userStore.isSchoolAdmin && visibilityStore.analyticsDashboardVisibility) || userStore.isSuperAdmin || userStore.isGoalbookBusinessUser" class="router-link" as-child>
              <router-link to="/admin/analytics" @click="analytics.sendEvent('Clicked Analytics Dashboard', { ref: 'navbar' })">Analytics Dashboard</router-link>
            </NavigationMenuLink>
            <AdminToggle class="ml-0 px-2.5 py-2" flagType="beta" v-if="userStore.isGoalbookBusinessUser" />
            <NavigationMenuLink v-if="hasPathwaysAccess" class="router-link" :href="pathwaysBaseUrl" @click="analytics.sendEvent('Clicked Goalbook Pathways', { ref: 'navbar' })">
              Switch to <span class="font-bold">Goalbook Pathways</span>
            </NavigationMenuLink>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
      </nav>
      </NavigationMenu>
    </div>
  </div>
  <Suspense><ContentProfileSelectorContainer /></Suspense>
  <Suspense><LicenseSelectorContainer /></Suspense>
  <VisibilitySettingsModal v-model:is-open="visibilityModalOpen" />
  <CustomizationDemoModal v-if="showCustomizationDemos" v-model:is-open="customizationDemoModalOpen" />
  
  <div class="flex md:hidden w-full h-full items-stretch bg-blue-400 z-50">
    <a
      :href="homeUrl"
      class="hover:bg-blue-500 focus:bg-blue-500 relative flex min-w-max shrink-0 items-center text-nowrap h-full px-3 transition-all duration-200"
    >
      <img class="h-8 pr-2" src="./../assets/toolkit_circle.png" alt="" />
      <span class="text-white text-xl">Goalbook Toolkit</span>
    </a>
    <div class="ml-auto flex items-center pr-3">
      <button @click="mobileMenuOpen = !mobileMenuOpen" class="text-white hover:bg-blue-500 p-2 rounded-md flex items-center justify-center">
        <span class="material-icons-round text-2xl">{{ mobileMenuOpen ? 'close' : 'menu' }}</span>
      </button>
    </div>
  </div>
  
  <!-- Full-width mobile menu that appears below the header -->
  <div v-if="mobileMenuOpen" class="mobile-menu">
    <div class="w-full">
      <NavMenuItems 
        :is-hamburger="true"
      />
    </div>
    
    <!-- User Menu -->
    <div class="border-t border-white opacity-50 my-1"></div>
    <div class="mobile-menu-trigger cursor-pointer" @click="userMenuOpen = !userMenuOpen">
      <div class="flex items-center">
        <img
          :src="profileImageUrl"
          alt=""
          v-if="profileImageUrl"
          class="inline h-7 w-7 mr-2"
        />
        <div class="flex flex-col text-left">
          <span class="leading-none fs-exclude">{{ userName }}</span>
          <small class="text-xs leading-3 text-white fs-exclude">{{ userTitle }}</small>
        </div>
      </div>
      <span class="material-icons-round text-text-2xl">arrow_drop_down</span>
    </div>
    <!-- User Menu Dropdown Content -->
    <div v-if="userMenuOpen" class="mobile-menu-content">
      <button v-if="showContentProfileSelector" @click="setIsProfileSelectorOpen(true)" class="mobile-menu-link">{{userStore.isSuperAdmin || userStore.isTestUser || userStore.isGoalbookBusinessUser ? 'Content Profile Selector' : 'Select Your State'}}</button>
      <button v-if="userStore.isSuperAdmin || userStore.isTestUser || userStore.isGoalbookBusinessUser" @click="handleOpenVisibilityModal" class="mobile-menu-link">Visibility Settings</button>
      <button v-if="showCustomizationDemos" @click="handleOpenCustomizationDemoModal" class="mobile-menu-link">Customization Demos</button>
      <button v-if="userStore.isTestUser" @click="userStore.setLicenseSelectorOpen(true)" class="mobile-menu-link">License Selector</button>
      <div class="my-1 h-px bg-gray-200"></div>
      <a v-if="!userStore.isSuperAdmin" :href="accountUrl" class="mobile-menu-link" @click="analytics.sendEvent('Clicked Account Settings', { ref: 'navbar' })">Account Settings</a>
      <router-link to="/library/all" class="mobile-menu-link" @click="analytics.sendEvent('Clicked My Library', { ref: 'navbar' })">My Library</router-link>
      <a :href="signOutUrl" class="mobile-menu-link" @click="analytics.sendEvent('Clicked Sign Out', { ref: 'navbar' })">Sign Out</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenu, NavigationMenuTrigger } from '@/components/navigation-menu'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import AdminToggle from './AdminToggle.vue'
import ContentProfileSelectorContainer from './../contentprofile/ContentProfileSelectorContainer.vue'
import LicenseSelectorContainer from './LicenseSelectorContainer.vue'
import VisibilitySettingsModal from '@/visibility/VisibilitySettingsModal.vue'
import CustomizationDemoModal from '@/customization-demo/CustomizationDemoModal.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import NavMenuItems from './NavMenuItems.vue'
import { isBetaOptIn, withBetaOptIn } from '@/util/beta_opt_in'
import ProgressSpinner from 'primevue/progressspinner'

interface User {
  title?: string;
  name: string;
  id: number | string;
  show_training_button?: boolean;
}

const userStore = useUserStore()
const visibilityStore = useVisibilityStore()
const profileStore = useContentProfileStore()
const { profileImageUrl } = storeToRefs(profileStore)
const { setIsProfileSelectorOpen } = profileStore

const mobileMenuOpen = ref(false)
const userMenuOpen = ref(false)
const visibilityModalOpen = ref(false)
const customizationDemoModalOpen = ref(false)

const toolkitBaseUrl = import.meta.env.VITE_TOOLKIT_URL
const pathwaysBaseUrl = import.meta.env.VITE_PATHWAYS_URL
const accountUrl = `${toolkitBaseUrl}/account`
const signOutUrl = `${toolkitBaseUrl}/sign_out`
const route = useRoute()
const homeUrl = computed(() => {
  const isBetaSession = isBetaOptIn(route.query) || visibilityStore.betaVisibility
  return isBetaSession ? withBetaOptIn(`${toolkitBaseUrl}/v/?ref=navbar`) : `${toolkitBaseUrl}/v`
})
const hasPathwaysAccess = computed(() => userStore.pathways_access || userStore.isSuperAdmin)

const userName = computed(() => userStore.userName || '')
const userTitle = computed(() => (userStore.user as User)?.title || '')

const hasBetaAccess = computed(
  () => (userStore.isSuperAdmin || userStore.isTestUser) && !userStore.isGoalbookBusinessUser
)
const showContentProfileSelector = computed(
  () => userStore.isSuperAdmin || userStore.isTestUser || userStore.isGoalbookBusinessUser || visibilityStore.canChangeProfile
)

const analytics = getAnalyticsTracker()

const showCustomizationDemos = computed(
  () => userStore.isSuperAdmin || userStore.isTestUser || userStore.isGoalbookBusinessUser
)

const handleOpenVisibilityModal = () => {
  visibilityModalOpen.value = true
  mobileMenuOpen.value = false
  userMenuOpen.value = false
}

const handleOpenCustomizationDemoModal = () => {
  customizationDemoModalOpen.value = true
  mobileMenuOpen.value = false
  userMenuOpen.value = false
}
</script>

<style scoped lang="postcss">
.router-link {
  @apply block w-full text-left select-none py-2 px-2.5 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground;
}
.mobile-menu {
  @apply fixed top-[49px] left-0 right-0 bg-blue-400 w-full z-40 shadow-lg overflow-auto max-h-[80vh] md:hidden;
}

.mobile-menu-trigger, :deep(.mobile-menu-trigger) {
  @apply flex items-center justify-between w-full px-2 py-3 text-white hover:bg-blue-500;
}

.mobile-menu-link, :deep(.mobile-menu-link) {
  @apply block px-3 py-2 text-gray-800 hover:bg-gray-100 w-full text-left;
}

.mobile-menu-content {
  @apply bg-white py-1 w-full;
}

.mobile-menu-item {
  @apply w-full;
}
</style>